<template>
  <!-- DAY MODE: unchanged v-calendar behavior -->
  <DatePicker
    v-if="granularity === 'day'"
    v-model="date"
    @dayclick="(_, event) => { event.target.blur(); }"
    @update:modelValue="$emit('update:modelValue', $event)"
  >
    <template #default="{ togglePopover, inputValue }">
      <b-text-field
        :label="props.label"
        :placeholder="props.placeholder"
        :model-value="inputValue"
        readonly
        @click="togglePopover"
      />
    </template>
  </DatePicker>

  <!-- MONTH MODE: custom month+year panel -->
  <div v-else ref="triggerRef">
    <b-text-field
      :label="props.label"
      :placeholder="props.placeholder"
      :model-value="displayLabel"
      readonly
      @click="isOpen = !isOpen"
    />
    <!-- Teleported to <body> with fixed positioning so the panel escapes the
         modal's `overflow-y-auto` content area (which otherwise clips it).
         Mirrors the BSelect dropdown pattern. -->
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="panelRef"
        :style="panelStyle"
        class="fixed z-[100] w-72 rounded-xl border border-[#373737] bg-[#1a1a1a] p-3 shadow-xl"
      >
        <!-- header -->
      <div class="flex items-center justify-between mb-3">
        <button type="button" class="px-2 py-1 text-white hover:bg-[#373737] rounded" @click="stepYear(-1)">‹</button>
        <button type="button" data-e2e="datepicker-year-toggle" class="px-3 py-1 text-white font-medium hover:bg-[#373737] rounded" @click="view = view === 'month' ? 'year' : 'month'">
          {{ view === 'month' ? panelYear : `${yearPageStart} - ${yearPageStart + 11}` }}
        </button>
        <button type="button" class="px-2 py-1 text-white hover:bg-[#373737] rounded" @click="stepYear(1)">›</button>
      </div>

      <!-- month grid -->
      <div v-if="view === 'month'" class="grid grid-cols-3 gap-2">
        <button
          v-for="m in 12"
          :key="m"
          type="button"
          :data-e2e="`datepicker-month-${m - 1}`"
          :class="[
            'py-2 text-sm rounded capitalize',
            isSelectedMonth(m - 1) ? 'bg-primary text-white' : 'text-white hover:bg-[#373737]'
          ]"
          @click="selectMonth(m - 1)"
        >
          {{ monthLabels[m - 1] }}
        </button>
      </div>

      <!-- year grid -->
      <div v-else class="grid grid-cols-3 gap-2">
        <button
          v-for="y in yearPage"
          :key="y"
          type="button"
          :data-e2e="`datepicker-year-${y}`"
          :class="[
            'py-2 text-sm rounded',
            y === panelYear ? 'bg-primary text-white' : 'text-white hover:bg-[#373737]'
          ]"
          @click="selectYear(y)"
        >
          {{ y }}
        </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, computed, onErrorCaptured } from 'vue';
import { DatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import { useElementBounding, onClickOutside } from '@vueuse/core';
import BTextField from './BTextField.vue';

interface Props {
  label?: string;
  placeholder?: string;
  modelValue: Date | null;
  granularity?: 'day' | 'month';
}

const props = withDefaults(defineProps<Props>(), { granularity: 'day' });
const emit = defineEmits(['update:modelValue']);

const { locale } = useI18n();

// --- day mode (unchanged) ---
const date = ref(props.modelValue);
watch(() => props.modelValue, (newValue) => { date.value = newValue; });

// --- month mode ---
const now = new Date();
const panelYear = ref(props.modelValue ? props.modelValue.getUTCFullYear() : now.getUTCFullYear());
const view = ref<'month' | 'year'>('month');
const isOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);

// The panel is teleported to <body>, so it lives outside `triggerRef` in the
// DOM. Close on any click outside the panel, but ignore the trigger itself
// (its own @click handles the toggle).
onClickOutside(panelRef, () => {
  isOpen.value = false;
  view.value = 'month';
}, { ignore: [triggerRef] });

// Position the fixed panel relative to the trigger's viewport box. Flip above
// when there isn't room below (the experience-date fields sit near the modal
// bottom), and clamp horizontally so a full-width panel never leaves the
// viewport. Mirrors the BSelect dropdown positioning.
const PANEL_HEIGHT = 320;
const PANEL_WIDTH = 288; // w-72
const triggerBox = useElementBounding(triggerRef);
const panelStyle = computed(() => {
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
  const spaceBelow = vh - triggerBox.bottom.value;
  const dropUp = spaceBelow < PANEL_HEIGHT && triggerBox.top.value > spaceBelow;
  const left = Math.max(8, Math.min(triggerBox.left.value, vw - PANEL_WIDTH - 8));
  return dropUp
    ? { bottom: `${vh - triggerBox.top.value + 4}px`, left: `${left}px` }
    : { top: `${triggerBox.bottom.value + 4}px`, left: `${left}px` };
});

watch(() => props.modelValue, (v) => {
  if (v) panelYear.value = v.getUTCFullYear();
});

const monthLabels = computed(() => {
  const fmt = new Intl.DateTimeFormat(locale.value || 'fr', { month: 'short', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(Date.UTC(2020, i, 1))));
});

const displayLabel = computed(() => {
  if (!props.modelValue) return '';
  return new Intl.DateTimeFormat(locale.value || 'fr', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(props.modelValue);
});

const isSelectedMonth = (monthIndex: number) =>
  !!props.modelValue &&
  props.modelValue.getUTCFullYear() === panelYear.value &&
  props.modelValue.getUTCMonth() === monthIndex;

const stepYear = (delta: number) => {
  if (view.value === 'year') { panelYear.value += delta * 12; }
  else { panelYear.value += delta; }
};

const yearPageStart = computed(() => panelYear.value - (panelYear.value % 12));
const yearPage = computed(() => Array.from({ length: 12 }, (_, i) => yearPageStart.value + i));

const selectMonth = (monthIndex: number) => {
  emit('update:modelValue', new Date(Date.UTC(panelYear.value, monthIndex, 1)));
  isOpen.value = false;
  view.value = 'month';
};

const selectYear = (year: number) => {
  panelYear.value = year;
  view.value = 'month';
};

// v-calendar 3.1.2 internal bug (day mode): DateRangeContext.render reads
// days[0].dayIndex without an empty check. Suppress it here.
onErrorCaptured((err) => {
  if (err instanceof TypeError && err.message?.includes('dayIndex')) {
    return false;
  }
});
</script>
